// #region Imports
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import Guid from "_shared/guids/guid";
import { useMemo, useState } from "react";
import UserCoworkerQuery, {
  UserCoworkerFilterParameters,
  UserCoworkerOrderParameters,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import { useGetUserCoworkersQuery } from "users/information/coworker-information/_api/user-coworker-api";
import UserCoworker from "users/information/coworker-information/user-coworker";
import UserInformation from "users/information/user-information";
import { useGetUserNetworkConnectionsQuery } from "users/networking/connections/_api/user-network-connection-api";
import CreateNetworkedGroupApiRequest from "users/networking/groups/_api/commands/create-networked-group-api-command";
import NetworkedGroupSelector from "users/networking/groups/_views/networked-group-selector";
// #endregion

// #region Styled Components
const ContentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    columnCount: 1,
  },
  [theme.breakpoints.up("md")]: {
    columnCount: 2,
  },
  [theme.breakpoints.up("lg")]: {
    columnCount: 3,
  },
  columnFill: "balance",
  columnGap: theme.spacing(4),
  gridColumn: "1 / auto",
  gridRowStart: "2",
  paddingTop: theme.spacing(0.75),
}));
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const StyledSubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: "50%",
}));
// #endregion

// #region Props
export interface CreateNetworkedGroupComponentProps {
  onCreate: (request: CreateNetworkedGroupApiRequest | null) => void;
}
// #endregion

// #region Component
export default function CreateNetworkedGroupComponent(props: Readonly<CreateNetworkedGroupComponentProps>) {
  const { onCreate } = props;

  // #region State
  const [groupName, setGroupName] = useState<string | undefined>();
  const [description, setDescription] = useState<string | undefined>();
  const [selectedMembers, setSelectedMembers] = useState<Guid[]>([]);

  // #region Queries
  const paginationParameters = PaginationParameters.create({ pageIndex: 0, pageSize: 100 });
  const filterParameters = new UserCoworkerFilterParameters();
  const orderParameters = new UserCoworkerOrderParameters();
  const query = new UserCoworkerQuery(filterParameters, orderParameters, paginationParameters);

  const coworkerQuery = useGetUserCoworkersQuery(query);
  const networkConnectionQuery = useGetUserNetworkConnectionsQuery();
  
  const networkConnections = useMemo(() => {
    if (!networkConnectionQuery.data) return [];
    return UserInformation.fromJSONArray(networkConnectionQuery.data);
  }, [networkConnectionQuery.data]);

  const coworkers = useMemo(() => {
    if (!coworkerQuery.data?.data) {
      return PaginatedResponse.empty<UserCoworker>();
    }
    return PaginatedResponse.from<UserCoworker>(
      UserCoworker.fromJSONArray(coworkerQuery.data.data),
      coworkerQuery.data.headers
    );
  }, [coworkerQuery.data]);
  // #endregion

  // #region Functions
  function generateDefaultGroupName(): string {
    if (selectedMembers.length === 0) return "New Group";
    const selectedMemberNames = selectedMembers.map((id) => {
      const coworker = coworkers.data.find((c) => c.userId === id);
      const connection = networkConnections.find((c) => c.id.isEqualTo(id));
      return coworker?.name ?? connection?.name ?? "Unknown User";
    });
    return `Chat with ${selectedMemberNames.join(", ")}`;
  }
  // #endregion

  // #region Render
  return (
    <div>
      <ContentContainer>
        <StyledTextField
          label="Group Name"
          variant="outlined"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
        />
        <StyledTextField
          label="Description"
          variant="outlined"
          multiline
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <NetworkedGroupSelector
          coworkers={coworkers.data}
          networkConnections={networkConnections}
          selectedMemberUserIds={selectedMembers}
          onMemberSelected={(id: Guid) => {
            setSelectedMembers((prev) => {
              if (prev.some((memberId) => memberId.value === id.value)) {
                return prev.filter((memberId) => memberId.value !== id.value);
              }
              return [...prev, id];
            });
          }}
        />
      </ContentContainer>
      <StyledSubmitButton
        variant="contained"
        color="primary"
        disabled={selectedMembers.length === 0}
        onClick={() => {
          const request = new CreateNetworkedGroupApiRequest(
            groupName ?? generateDefaultGroupName(),
            description,
            selectedMembers
          );
          onCreate(request);
        }}
      >
        Create Group
      </StyledSubmitButton>
    </div>
  );
  // #endregion
}
